import type { FC, ReactNode, RefObject } from 'react'

import { useEffect, useRef } from 'react'
import TransitionLink from '@/components/motion/transition-link'
import LocalTime from '@/components/generals/local-time'
import { useLenis } from '@/components/motion/smooth-scroll'
import { gsap, prefersReducedMotion, useGSAP } from '@/lib/motion'
import { contact, navItems } from '@/constants/site'

interface MenuProps {
    open: boolean
    onClose: () => void
    toggleRef: RefObject<HTMLButtonElement | null>
}

const MENU_ITEMS = [{ label: 'Home', href: '/' }, ...navItems]

// Full-screen navigation for small screens. It wipes down from the top, the
// links rise out of their masks, and page scrolling pauses while it is open.
const Menu: FC<MenuProps> = ({ open, onClose, toggleRef }): ReactNode => {
    const panelRef = useRef<HTMLDivElement>(null)
    const mountedRef = useRef(false)
    const lenis = useLenis()

    useEffect(() => {
        if (!lenis) return

        if (open) lenis.stop()
        else lenis.start()
    }, [open, lenis])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                onClose()
                toggleRef.current?.focus()
            }
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open, onClose, toggleRef])

    useGSAP(() => {
        const panel = panelRef.current

        if (!panel) return

        if (!mountedRef.current) {
            mountedRef.current = true
            return
        }

        const instant = prefersReducedMotion()

        if (open) {
            gsap.set(panel, { visibility: 'visible' })

            gsap.timeline()
                .fromTo(panel, { clipPath: 'inset(0% 0% 100% 0%)' }, {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: instant ? 0 : 0.8,
                    ease: 'expo.inOut'
                })
                .fromTo('[data-menu-item]', { yPercent: instant ? 0 : 110 }, {
                    yPercent: 0,
                    duration: instant ? 0 : 0.9,
                    ease: 'expo.out',
                    stagger: 0.06
                }, instant ? 0 : 0.28)

            panel.querySelector<HTMLElement>('a')?.focus({ preventScroll: true })
        }
        else {
            gsap.to(panel, {
                clipPath: 'inset(0% 0% 100% 0%)',
                duration: instant ? 0 : 0.65,
                ease: 'expo.inOut',
                onComplete: () => {
                    gsap.set(panel, { visibility: 'hidden' })
                }
            })
        }
    }, { dependencies: [open], scope: panelRef })

    return (
        <div
            id='site-menu'
            ref={panelRef}
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
            inert={!open}
            data-theme='ink'
            data-lenis-prevent
            className='invisible fixed inset-0 z-40 flex flex-col overflow-y-auto bg-canvas px-5 pb-8 pt-24 text-fore md:px-8'
            style={{ clipPath: 'inset(0% 0% 100% 0%)' }}
        >
            <nav aria-label='Menu'>
                <ul className='border-t border-rule'>
                    {MENU_ITEMS.map((item, index) => (
                        <li key={item.href} className='overflow-hidden border-b border-rule'>
                            <TransitionLink
                                href={item.href}
                                data-menu-item=''
                                className='flex items-baseline justify-between py-3'
                            >
                                <span className='type-h1'>
                                    {item.label}
                                </span>

                                <span className='type-label text-quiet'>
                                    0{index + 1}
                                </span>
                            </TransitionLink>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className='mt-auto grid gap-8 pt-12 sm:grid-cols-2'>
                <div className='space-y-2'>
                    <p className='type-label text-quiet'>
                        Say hello
                    </p>

                    <a href={`mailto:${contact.email}`} className='block text-lg'>
                        {contact.email}
                    </a>

                    <a href={contact.phoneHref} className='block text-lg'>
                        {contact.phone}
                    </a>
                </div>

                <div className='space-y-2'>
                    <p className='type-label text-quiet'>
                        Studios
                    </p>

                    {contact.studios.map(studio => (
                        <p key={studio.city} className='flex justify-between gap-6 text-lg'>
                            <span>
                                {studio.city}
                            </span>

                            <LocalTime zone={studio.zone} className='font-mono text-sm text-quiet' />
                        </p>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Menu
